import type { AssertionError } from 'assert';
import assert from 'assert';

import type {
  FragmentContext,
  WorkspaceRequired,
} from '@fragment/lambda-utils';
import type {
  AccountCurrencyCode,
  CurrencyCode,
  Day,
  DayIndex,
  Hour,
  HourIndex,
  LedgerAccountType,
  Month,
  MonthIndex,
  NonEmptyArray,
  Period,
  Quarter,
} from '@fragment/types';
import {
  accountCurrencyCodes,
  currencyCodes,
  days,
  hours,
  months,
  quarters,
} from '@fragment/types';
import type { FirstMoment } from '@fragment/types/time';

import type { RestifyHttpErrorOptions } from './HttpError';
import { InternalError } from './errors';

// Type for error constructors - matches VError's approach of maximum flexibility
// While we'd prefer stronger typing, TypeScript's structural type system makes it
// difficult to properly type error constructors without requiring static properties
// like captureStackTrace that our custom errors don't have
type ErrorConstructor = any;

type AssertOptions = RestifyHttpErrorOptions & {
  retryable?: boolean;
  message: string;
};

type MaybeLazy<T> = T | (() => T);

function assertError<T = unknown>(
  predicate: T,
  CustomError: ErrorConstructor,
  options: MaybeLazy<AssertOptions>
): asserts predicate is NonNullable<T> {
  if (CustomError === undefined) {
    throw new InternalError('you must pass in CustomError to assert');
  }

  try {
    assert(!!predicate);
  } catch (cause) {
    const opts = typeof options === 'function' ? options() : options;
    // Node.js's `assert` module always throws `AssertionError`
    // see: https://nodejs.org/api/assert.html#class-assertassertionerror
    throw new CustomError(
      {
        ...opts,
        cause: cause as AssertionError,
      },
      opts.message
    );
  }
}
export default assertError;

export function assertEquals<T>(
  value: unknown,
  expected: T,
  CustomError: ErrorConstructor,
  options: MaybeLazy<AssertOptions>
): asserts value is T {
  if (CustomError === undefined) {
    throw new InternalError('you must pass in CustomError to assert');
  }
  try {
    assert(value === expected);
  } catch (cause: unknown) {
    const opts = typeof options === 'function' ? options() : options;
    // Node.js's `assert` module always throws `AssertionError`
    // see: https://nodejs.org/api/assert.html#class-assertassertionerror
    throw new CustomError(
      {
        ...opts,
        cause: cause as AssertionError,
      },
      opts.message
    );
  }
}

// useful when you want to narrow the type of something and error if something is the expected type
// useful in unions
export function assertIsNot<Value, Expected extends Value>(
  value: Value,
  expected: Expected,
  CustomError: ErrorConstructor,
  options: MaybeLazy<AssertOptions>
): asserts value is Exclude<Value, Expected> {
  if (CustomError === undefined) {
    throw new InternalError('you must pass in CustomError to assert');
  }
  try {
    assert(value !== expected);
  } catch (cause: unknown) {
    const opts = typeof options === 'function' ? options() : options;
    // Node.js's `assert` module always throws `AssertionError`
    // see: https://nodejs.org/api/assert.html#class-assertassertionerror
    throw new CustomError(
      {
        ...opts,
        cause: cause as Error,
      },
      opts.message
    );
  }
}

export function assertIsIn<T extends object, K extends PropertyKey>(
  value: T,
  key: K,
  options: RestifyHttpErrorOptions = {}
): asserts value is Extract<T, Record<K, unknown>> {
  if (!(key in value)) {
    throw new InternalError({
      ...options,
      info: {
        ...options.info,
        value,
      },
      message: `key ${String(key)} is not in value`,
    });
  }
}

export function assertUnreachable(
  _neverValue: never,
  CustomError: ErrorConstructor
): never {
  if (CustomError === undefined) {
    throw new InternalError('you must pass in CustomError to assert');
  }

  throw new CustomError(`reached unreachable code`);
}

export function assertLedgerAccountType(
  type: string
): asserts type is LedgerAccountType {
  return assertError(
    ['asset', 'liability', 'income', 'expense'].includes(type),
    InternalError,
    {
      message: 'ledger account type invalid',
    }
  );
}

export function assertAccountCurrencyCode(
  candidate: string
): asserts candidate is AccountCurrencyCode {
  return assertError(
    accountCurrencyCodes.includes(candidate as AccountCurrencyCode),
    InternalError,
    () => ({
      info: {
        candidate,
      },
      message: `account currency code invalid: ${candidate}`,
    })
  );
}

export function assertCurrencyCode(
  candidate: string,
  CustomError: ErrorConstructor = InternalError,
  message?: string
): asserts candidate is CurrencyCode {
  return assertError(
    currencyCodes.includes(candidate as CurrencyCode),
    CustomError,
    () => ({
      message: message ?? `currency code invalid: ${candidate}`,
    })
  );
}

export function assertHour(candidate: string): asserts candidate is Hour {
  return assertError(hours.includes(candidate as Hour), InternalError, () => ({
    message: `hour invalid: ${candidate}`,
  }));
}

export function assertHourIndex(
  candidate: number
): asserts candidate is HourIndex {
  return assertError(candidate >= 0 && candidate <= 23, InternalError, () => ({
    message: `hour index invalid: ${candidate}`,
  }));
}

export function assertDay(candidate: string): asserts candidate is Day {
  return assertError(days.includes(candidate as Day), InternalError, () => ({
    message: `day invalid: ${candidate}`,
  }));
}

export function assertDayIndex(
  candidate: number
): asserts candidate is DayIndex {
  return assertError(candidate >= 1 && candidate <= 31, InternalError, () => ({
    message: `day index invalid: ${candidate}`,
  }));
}

export function assertMonth(candidate: string): asserts candidate is Month {
  return assertError(
    months.includes(candidate as Month),
    InternalError,
    () => ({
      message: `month invalid: ${candidate}`,
    })
  );
}

export function assertMonthIndex(
  candidate: number
): asserts candidate is MonthIndex {
  return assertError(candidate >= 1 && candidate <= 12, InternalError, () => ({
    message: `month index invalid: ${candidate}`,
  }));
}

export function assertQuarter(candidate: string): asserts candidate is Quarter {
  return assertError(
    quarters.includes(candidate as Quarter),
    InternalError,
    () => ({
      message: `quarter invalid: ${candidate}`,
    })
  );
}

export function assertPeriod(period: Period): asserts period is Period {
  const validPeriod = ['year', 'month', 'quarter', 'day', 'hour'].includes(
    period.type
  );
  const year = parseInt(period.year, 10);
  const validYear = !Number.isNaN(year) && year > 0;
  const validQuarter =
    period.type === 'quarter' ? quarters.includes(period.quarter) : true;
  const validMonth =
    period.type === 'month' ? months.includes(period.month) : true;
  const validDay =
    period.type === 'day'
      ? days.includes(period.day) && months.includes(period.month)
      : true;
  const validHour =
    period.type === 'hour'
      ? hours.includes(period.hour) &&
        days.includes(period.day) &&
        months.includes(period.month)
      : true;
  return assertError(
    validPeriod &&
      validYear &&
      validMonth &&
      validQuarter &&
      validDay &&
      validHour,
    InternalError,
    () => ({
      message: `Unsupported period type: ${period.type}`,
    })
  );
}

export function assertFirstMoment(
  moment: FirstMoment
): asserts moment is FirstMoment {
  const validPeriod = ['year', 'month', 'quarter', 'day', 'hour'].includes(
    moment.type
  );
  const year = parseInt(moment.year, 10);
  const validYear = !Number.isNaN(year) && year > 0;
  const validQuarter =
    moment.type === 'quarter' ? quarters.includes(moment.quarter) : true;
  const validMonth =
    moment.type === 'month' ? months.includes(moment.month) : true;
  const validDay =
    moment.type === 'day'
      ? days.includes(moment.day) && months.includes(moment.month)
      : true;
  const validHour =
    moment.type === 'hour'
      ? hours.includes(moment.hour) &&
        days.includes(moment.day) &&
        months.includes(moment.month)
      : true;
  return assertError(
    validPeriod &&
      validYear &&
      validMonth &&
      validQuarter &&
      validDay &&
      validHour,
    InternalError,
    () => ({
      message: `Unsupported FirstMoment type: ${moment.type}`,
    })
  );
}

export function assertInteger(
  value: unknown
): asserts value is bigint | number {
  if (typeof value !== 'number' && typeof value !== 'bigint') {
    throw new TypeError(`Expected integer number or bigint. Got: ${value}`);
  }
  if (typeof value === 'number' && !Number.isInteger(value)) {
    throw new TypeError(`Expected integer. Got: ${value}`);
  }
}

export function assertIsError(
  value: unknown,
  options: RestifyHttpErrorOptions = {}
): asserts value is Error {
  if (!(value instanceof Error)) {
    throw new InternalError({
      ...options,
      info: {
        ...options.info,
        error: value,
      },
      message: 'value is not an Error',
    });
  }
}

export function assertWorkspaceContext<T extends FragmentContext>(
  context: T
): asserts context is WorkspaceRequired<T> {
  assertError(context.workspaceId, InternalError, () => ({
    message: `context.workspaceId not specified`,
  }));
}

export function assertNonEmptyArray<T>(
  arr: T[],
  CustomError: ErrorConstructor,
  options: MaybeLazy<AssertOptions>
): asserts arr is NonEmptyArray<T> {
  if (CustomError === undefined) {
    throw new InternalError('you must pass in CustomError to assert');
  }
  if (arr.length === 0) {
    const opts = typeof options === 'function' ? options() : options;
    throw new CustomError(opts, opts.message);
  }
}

/*

If you are adding an assertion function to this file, use a function declaration, not an assignment.

Don't use:
export const myAssertionFunc = (params) => {whatever}
export const myAssertionFunc = function

Use:
export function myAssertionFunc(params) {whatever}

Reference:  https://github.com/microsoft/TypeScript/issues/36931#issuecomment-789866374
*/
